import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { useTranslation } from "../../../utils/translation";
import {
  listUsers,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
  selectUsersUpdateError,
  selectUsersUpdateMessage,
  updateUserById,
} from "../userSlice";

const PAGE_SIZE = 10;

const UsersEditPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const users = useSelector(selectUsers);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const updateError = useSelector(selectUsersUpdateError);
  const updateMessage = useSelector(selectUsersUpdateMessage);
  const [permissionError, setPermissionError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formState, setFormState] = useState({
    fullName: "",
    role: "",
    businessId: "",
    state: false,
    email: "",
  });

  useEffect(() => {
    dispatch(listUsers());
  }, [dispatch]);

  const getFullName = (user) => {
    if (user?.fullName) return user.fullName;
    const nameParts = [user?.name, user?.surName].filter(Boolean);
    return nameParts.length ? nameParts.join(" ") : "-";
  };

  const getEmail = (user) => {
    return user?.email || user?.userEmail || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users || [];
    return (users || []).filter((user) => {
      const name = getFullName(user).toLowerCase();
      return name.includes(term);
    });
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const openEditModal = (user) => {
    const fullName = getFullName(user);
    const role = user?.role || user?.userType || "";
    const businessId = user?.businessId || "";
    const state = Boolean(user?.state);
    const email = getEmail(user);
    setEditUser(user);
    setFormState({ fullName, role, businessId, state, email });
    setShowEdit(true);
  };

  const closeEditModal = () => {
    setShowEdit(false);
    setEditUser(null);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const isEditDisabled = useMemo(() => {
    return !editUser || !editUser.id;
  }, [editUser]);

  const handleSave = async () => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    if (!editUser?.id) return;
    setPermissionError("");
    const payload = {
      fullName: formState.fullName?.trim() || "",
      role: formState.role?.trim() || "",
      businessId: formState.businessId?.trim() || "",
      state: Boolean(formState.state),
    };
    try {
      await dispatch(updateUserById({ id: editUser.id, data: payload })).unwrap();
      await dispatch(listUsers());
      closeEditModal();
    } catch (error) {
      // Error is handled in slice state; keep modal open
    }
  };

  const renderRows = () => {
    if (isLoading && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="4" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!users || users.length === 0) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    if (!pagedUsers || pagedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return pagedUsers.map((user) => {
      const fullName = getFullName(user);
      return (
      <tr key={user.id || user.userId}>
        <td>{highlightMatch(fullName, search.trim())}</td>
        <td>{getEmail(user)}</td>
        <td>{user?.role || user?.userType || "-"}</td>
        <td>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => openEditModal(user)}
            disabled={!user?.id || !adminAccess}
          >
            {t("admin_common.edit")}
          </button>
        </td>
      </tr>
      );
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{t("admin_users_pages.edit_title")}</h4>
              {permissionError && (
                <div className="alert alert-danger">{permissionError}</div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>{t("admin_common.role")}</th>
                      <th>{t("admin_common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filteredUsers.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal className="modal fade" show={showEdit} onHide={closeEditModal}>
        <div role="document">
          <div>
            <form>
              <div className="modal-header">
                <h4 className="modal-title fs-20">{t("admin_users_pages.edit_title")}</h4>
                <button type="button" className="btn-close" onClick={closeEditModal}>
                  <span></span>
                </button>
              </div>
              <div className="modal-body">
                {updateError && (
                  <div className="text-danger mb-2">
                    {updateMessage || t("admin_users_pages.update_failed")}
                  </div>
                )}
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_common.name")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="fullName"
                    value={formState.fullName}
                    onChange={handleFormChange}
                    placeholder={t("admin_common.name")}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_common.email")}</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formState.email}
                    readOnly
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_common.role")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="role"
                    value={formState.role}
                    onChange={handleFormChange}
                    placeholder={t("admin_common.role")}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_users_pages.business_id_optional")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="businessId"
                    value={formState.businessId}
                    onChange={handleFormChange}
                    placeholder={t("admin_common.business_id")}
                  />
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="edit-is-active"
                    name="state"
                    checked={Boolean(formState.state)}
                    onChange={handleFormChange}
                  />
                  <label className="form-check-label" htmlFor="edit-is-active">
                    {formState.state ? t("admin_common.status_active") : t("admin_common.status_passive")}
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={closeEditModal}>
                  {t("admin_common.cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isEditDisabled}
                >
                  {t("admin_common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UsersEditPage;
